import { Link } from 'react-router';

export function Privacy() {
  return (
    <div className="min-h-screen bg-surface-0">
      {/* Header */}
      <header className="border-b border-[var(--border)] bg-[var(--surface-1)]">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2.5 text-text-primary hover:text-text-secondary transition-colors">
            <img src="/logo.png" alt="Palaryn" width="22" height="22" />
            <span className="text-sm font-medium">Palaryn</span>
          </Link>
          <Link to="/terms" className="text-sm text-text-tertiary hover:text-text-secondary transition-colors">
            Terms of Service
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="mx-auto max-w-4xl px-6 py-16">
        <h1 className="text-3xl font-bold text-text-primary">Privacy Policy</h1>
        <p className="mt-2 text-sm text-text-tertiary">Last updated: February 24, 2026</p>

        <div className="mt-10 space-y-8 text-sm leading-relaxed text-text-secondary">
          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">1. Introduction</h2>
            <p>
              Palaryn ("we", "us", "our") respects your privacy. This Privacy Policy explains how we collect,
              use, and protect information when you use the Palaryn platform ("Service").
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">2. Information We Collect</h2>
            <p className="font-medium text-text-primary mt-3">Account Information</p>
            <p>Email address, display name, and authentication credentials when you create an account.</p>

            <p className="font-medium text-text-primary mt-3">Billing Information</p>
            <p>
              Payment details are processed by Stripe. We store your Stripe customer ID and subscription status
              but do not store credit card numbers or bank details on our servers.
            </p>

            <p className="font-medium text-text-primary mt-3">Tool Call Data</p>
            <p>
              When AI agents route tool calls through Palaryn, we process the tool call metadata
              (tool name, actor, workspace, timestamps) and the request/response payloads as needed
              for policy evaluation, DLP scanning, and audit logging. DLP scanning automatically
              detects and redacts secrets and PII according to your configuration.
            </p>

            <p className="font-medium text-text-primary mt-3">Usage Data</p>
            <p>
              We collect aggregate usage metrics (request counts, latency, error rates) for billing,
              monitoring, and service improvement.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">3. How We Use Your Information</h2>
            <ul className="list-disc list-inside space-y-1">
              <li>Provide and operate the Service (policy evaluation, DLP, budgets, audit)</li>
              <li>Process billing and manage subscriptions via Stripe</li>
              <li>Enforce plan limits and usage quotas</li>
              <li>Generate audit logs and traces for your compliance needs</li>
              <li>Detect anomalies and security threats</li>
              <li>Communicate with you about your account, service updates, and support</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">4. Data Retention</h2>
            <p>
              Audit logs are retained based on your plan tier: 7 days (Free), 30 days (Pro),
              90 days (Business), or 365 days (Enterprise). After the retention period, logs are
              permanently deleted. Account data is retained while your account is active and deleted
              upon account termination per our retention schedule.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">5. Data Sharing</h2>
            <p>We do not sell your data. We share information only in these circumstances:</p>
            <ul className="mt-2 list-disc list-inside space-y-1">
              <li><strong>Stripe</strong> — for payment processing</li>
              <li><strong>Your configured external services</strong> — tool call payloads are forwarded to the APIs your agents interact with</li>
              <li><strong>Legal requirements</strong> — when required by law or to protect our rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">6. Security</h2>
            <p>
              We implement industry-standard security measures including encrypted transport (TLS),
              secret redaction via DLP scanning, JWT-based authentication, RBAC access controls,
              and append-only audit logs. API keys are hashed before storage.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">7. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="mt-2 list-disc list-inside space-y-1">
              <li>Access and export your data (audit logs, traces, account info)</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your account and associated data</li>
              <li>Object to processing for certain purposes</li>
            </ul>
            <p className="mt-2">
              To exercise these rights, contact us at{' '}
              <a href="mailto:privacy@palaryn.com" className="text-[var(--accent-teal)] hover:underline">
                privacy@palaryn.com
              </a>.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">8. Cookies</h2>
            <p>
              We use a session cookie (<code className="rounded bg-[var(--surface-2)] px-1.5 py-0.5 text-xs">palaryn_session</code>)
              to maintain your authenticated session. We do not use tracking cookies or third-party analytics.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">9. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy periodically. We will notify you of material changes via
              email or through the Service.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">10. Contact</h2>
            <p>
              For privacy-related questions, contact us at{' '}
              <a href="mailto:privacy@palaryn.com" className="text-[var(--accent-teal)] hover:underline">
                privacy@palaryn.com
              </a>.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
